import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import synraLogo from "@/assets/synra-logo.png";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, isRTL } = useLanguage();
  const location = useLocation();

  const navLinks = [
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.services"), href: "/services" },
    { label: t("nav.training"), href: "/training" },
    { label: t("nav.prompts"), href: "/prompts" },
    { label: t("nav.partners"), href: "/partners" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40" dir={isRTL ? "rtl" : "ltr"}>
      <div className="synra-container">
        {/* Row 1: Logo + Primary Nav */}
        <div className="hidden md:grid grid-cols-3 items-center h-16">
          {/* Left zone: Utility actions (appears on left in RTL, right in LTR — handled by dir) */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/auth">
              <Button variant="synra-ghost" size="sm" className="text-sm">
                {t("nav.signin")}
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="synra" size="sm" className="text-sm">
                {t("nav.getstarted")}
              </Button>
            </Link>
          </div>

          {/* Center zone: Navigation (always centered) */}
          <nav className="flex items-center justify-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "relative px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                  "hover:text-foreground hover:bg-accent/40",
                  isActive(link.href)
                    ? "text-primary bg-accent/30"
                    : "text-muted-foreground"
                )}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            ))}
          </nav>

          {/* Right zone: Logo (appears on right in RTL, left in LTR — handled by dir) */}
          <div className="flex items-center justify-end">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src={synraLogo}
                alt="SYNRA Logo"
                width={44}
                height={44}
                decoding="async"
                className="h-9 w-9 md:h-10 md:w-10 rounded-lg transition-transform duration-200 group-hover:scale-105"
              />
              <span className="font-heading text-lg md:text-xl font-bold text-foreground tracking-tight">
                SYNRA
              </span>
            </Link>
          </div>
        </div>

        {/* Mobile header */}
        <div className="flex md:hidden items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src={synraLogo}
              alt="SYNRA Logo"
              width={44}
              height={44}
              decoding="async"
              className="h-9 w-9 rounded-lg transition-transform duration-200 group-hover:scale-105"
            />
            <span className="font-heading text-lg font-bold text-foreground tracking-tight">
              SYNRA
            </span>
          </Link>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-foreground rounded-lg hover:bg-accent/30 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border/40 animate-fade-in">
          <div className="synra-container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive(link.href)
                    ? "text-primary bg-accent/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/20"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-border/40 flex flex-col gap-2">
              <div className="flex justify-center pb-1">
                <LanguageSwitcher />
              </div>
              <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                <Button variant="synra-outline" className="w-full">
                  {t("nav.signin")}
                </Button>
              </Link>
              <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                <Button variant="synra" className="w-full">
                  {t("nav.getstarted")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
